import { IconDownload } from "@tabler/icons-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "#app/components/ui/button.tsx";
import { DropdownMenuItem } from "#app/components/ui/dropdown-menu.tsx";
import {
  activatePwaUpdate,
  dismissPwaUpdate,
  promptPwaInstall,
  startPwa,
  usePwaState,
} from "#app/utils/pwa.ts";

/** Reusable in settings/header; menu=true uses the existing account menu primitive. */
export function InstallApp({ menu = false }: { menu?: boolean }) {
  const { install, installed, ios } = usePwaState();
  const { t } = useTranslation("common");
  if (installed || (!install && !ios)) return null;
  const onClick = () => {
    if (ios && !install) {
      toast(t("pwa.iosTitle"), {
        description: t("pwa.iosInstructions"),
        duration: 12_000,
      });
    } else {
      void promptPwaInstall().catch(() => toast.error(t("pwa.installError")));
    }
  };
  const content = (
    <>
      <IconDownload className="h-4 w-4" />
      <span>{t("pwa.install")}</span>
    </>
  );
  return menu ? (
    <DropdownMenuItem onClick={onClick}>{content}</DropdownMenuItem>
  ) : (
    <Button type="button" variant="outline" onClick={onClick}>
      {content}
    </Button>
  );
}

/**
 * Shares the existing themed Sonner instance and never forces a reload. Only
 * the installed app is told: a browser tab gets the new release on refresh.
 */
function PwaUpdateNotification() {
  const { waiting, dismissed, standalone } = usePwaState();
  const { t } = useTranslation("common");
  useEffect(() => {
    if (!waiting || dismissed || !standalone) return;
    const id = toast(t("pwa.updateTitle"), {
      id: "pwa-update",
      description: t("pwa.updateDescription"),
      duration: Infinity,
      action: {
        label: t("pwa.updateNow"),
        onClick: () => {
          dismissPwaUpdate();
          void activatePwaUpdate().catch(() =>
            toast.error(t("pwa.updateError")),
          );
        },
      },
      cancel: { label: t("pwa.later"), onClick: dismissPwaUpdate },
      onDismiss: dismissPwaUpdate,
    });
    return () => {
      toast.dismiss(id);
    };
  }, [waiting, dismissed, standalone, t]);
  return null;
}

export function PwaManager() {
  useEffect(() => startPwa(import.meta.env.PROD), []);
  return <PwaUpdateNotification />;
}
